import type { VaultMessageSummary } from './vault-api-messages.ts'

/**
 * Turning a flat page of messages into the conversations an operator
 * reads.
 *
 * The server has no thread endpoint and should not grow one: it already
 * returns every message of the root newest-first, and `threadId` is on
 * every row, so the grouping is a fold over a page we have already paid
 * for. Doing it here also keeps the "which of these are mine and unread"
 * question — which depends on who is looking — out of a cache that is
 * shared between operators.
 *
 * Pure, data-in/data-out, no React and no fetch: the ordering and the
 * unread arithmetic are the two things most likely to be quietly wrong,
 * and this is where they can be tested directly.
 */

export interface MessageThread {
  threadId: string
  /**
   * What the thread is called.
   *
   * The OLDEST message's subject, because that is the one that named the
   * conversation; replies routinely carry an empty subject, and taking
   * the newest would retitle a thread to "" the moment somebody replied.
   * Falls back through later subjects, and then to a placeholder, so a
   * thread whose opener is off the end of this page is still legible.
   *
   * UNTRUSTED TEXT, possibly written by an agent. Render as a string
   * child; never as markup.
   */
  subject: string
  /** The most recent message, which is what the list row shows. */
  latest: VaultMessageSummary
  /** Every message of this thread ON THIS PAGE, oldest first — the order
   * a conversation is read in. Not necessarily the whole thread: a page
   * is a page. */
  messages: VaultMessageSummary[]
  /** Messages on this page addressed to the viewer and not yet read.
   * Zero when the viewer's own principal is unknown — see
   * `unreadForPrincipal`. */
  unreadCount: number
  /** Distinct participant display names, for the list row's "who". */
  participants: string[]
}

/** The placeholder for a thread with no subject anywhere on the page. An
 * em dash would be read as a subject; this names the absence. */
const NO_SUBJECT = '(no subject)'

/** Oldest first. `createdAt` is an ISO-8601 UTC string, so a lexical
 * compare is a chronological compare; the id breaks ties, and because
 * ids are uuidv7 that tiebreak is itself chronological. */
function byCreatedAtAscending(
  left: VaultMessageSummary,
  right: VaultMessageSummary,
): number {
  if (left.createdAt !== right.createdAt)
    return left.createdAt < right.createdAt ? -1 : 1
  return left.id < right.id ? -1 : 1
}

/**
 * Messages addressed to `principalId` that have not been read.
 *
 * `null` yields an empty list rather than every unread message in the
 * root, and that is the load-bearing decision in this module. The
 * operator console is root-scoped: it can see an agent's mail. Counting
 * an agent's unread queue as the human's would badge the bell with work
 * that is not theirs and notify them about it, which is exactly the
 * burden D11 rules out. When we cannot tell which principal is the
 * viewer, the honest answer is "nothing for you", and the messages page
 * says why.
 */
export function unreadForPrincipal(
  messages: readonly VaultMessageSummary[],
  principalId: string | null,
): VaultMessageSummary[] {
  if (principalId === null) return []
  return messages.filter(
    (message) =>
      message.recipient.principalId === principalId && message.readAt === null,
  )
}

/**
 * Groups a page of messages into threads, newest conversation first.
 *
 * `viewerPrincipalId` is the operator's own vault principal, or `null`
 * when it could not be resolved — see `unreadForPrincipal` for what that
 * changes.
 */
export function groupIntoThreads(
  messages: readonly VaultMessageSummary[],
  viewerPrincipalId: string | null,
): MessageThread[] {
  const byThread = new Map<string, VaultMessageSummary[]>()
  for (const message of messages) {
    const existing = byThread.get(message.threadId)
    if (existing === undefined) byThread.set(message.threadId, [message])
    else existing.push(message)
  }

  const threads = [...byThread.entries()].map(([threadId, rows]) => {
    const ordered = [...rows].sort(byCreatedAtAscending)
    // `ordered` came from a non-empty bucket, so there is always a last
    // element — but `noUncheckedIndexedAccess` is on and an invariant
    // comment is not a type, so it is narrowed rather than asserted.
    const latest = ordered[ordered.length - 1] ?? ordered[0]
    if (latest === undefined)
      throw new Error(`thread ${threadId} grouped with no messages`)
    return {
      threadId,
      subject: ordered.find((row) => row.subject !== '')?.subject ?? NO_SUBJECT,
      latest,
      messages: ordered,
      unreadCount: unreadForPrincipal(ordered, viewerPrincipalId).length,
      participants: participantNames(ordered),
    }
  })

  // Newest conversation first: the thread whose latest message is most
  // recent is the one an operator came to look at.
  return threads.sort((left, right) =>
    byCreatedAtAscending(right.latest, left.latest),
  )
}

/** Distinct display names across both ends of every message, in the
 * order they first appear. A soft-deleted principal contributes its id,
 * because "who was this with" is still answerable. */
function participantNames(messages: readonly VaultMessageSummary[]): string[] {
  const names = new Set<string>()
  for (const message of messages) {
    names.add(message.sender.name ?? message.sender.principalId)
    names.add(message.recipient.name ?? message.recipient.principalId)
  }
  return [...names]
}
